import { colors, sans, tokens } from "../theme.ts";

// The surface primitives from docs/v2/02-system-design.md §3 and tokens.md:
// the page (ground + blobs), the card (glass, level 1), the float (heavier
// glass, level 2: nav, chips, toasts). Sheets and text inputs are opaque.
// Glass is never more than two layers deep.

export function GlassCard({
  children, style, padding, onClick,
}: { children?: React.ReactNode; style?: React.CSSProperties; padding?: string | number; onClick?: () => void }) {
  const g = colors.glass.card;
  return (
    <div
      onClick={onClick}
      style={{
        background: `rgba(${colors.glass.rgb}, ${g.alpha})`,
        backdropFilter: `blur(${g.blur}px) saturate(${g.saturate})`,
        WebkitBackdropFilter: `blur(${g.blur}px) saturate(${g.saturate})`,
        border: `1px solid ${g.border}`,
        boxShadow: `inset 0 1px 0 ${g.insetTop}, ${g.shadow}`,
        borderRadius: tokens.radius.card,
        padding,
        ...style,
      }}
    >{children}</div>
  );
}

export function GlassFloat({ children, style }: { children?: React.ReactNode; style?: React.CSSProperties }) {
  const g = colors.glass.float;
  return (
    <div style={{
      background: `rgba(${colors.glass.rgb}, ${g.alpha})`,
      backdropFilter: `blur(${g.blur}px) saturate(${g.saturate})`,
      WebkitBackdropFilter: `blur(${g.blur}px) saturate(${g.saturate})`,
      border: `1px solid ${g.border}`,
      boxShadow: `inset 0 1px 0 ${g.insetTop}, inset 0 -1px 0 ${g.insetBottom}, ${g.shadow}`,
      borderRadius: tokens.radius.pill,
      ...style,
    }}>{children}</div>
  );
}

/** Level 0: the cream ground with three soft blurred blobs, so the blur on the glass above has something to catch. */
export function Blobs() {
  return (
    <div aria-hidden style={{
      position: "fixed", top: 0, bottom: 0, left: "50%", transform: "translateX(-50%)",
      width: "min(560px, 100vw)", overflow: "hidden", zIndex: -1, background: colors.ground,
    }}>
      <Blob width="70%" left="-20%" top="-8%" color={colors.blobApricot} opacity={colors.blobOpacity} />
      <Blob width="60%" right="-18%" top="35%" color={colors.blobSage} opacity={colors.blobOpacity} />
      <Blob width="55%" left="20%" bottom="-15%" color={colors.blobMauve} opacity={colors.blobOpacity * 0.89} />
    </div>
  );
}

function Blob({
  width, left, right, top, bottom, color, opacity,
}: { width: string; left?: string; right?: string; top?: string; bottom?: string; color: string; opacity: number }) {
  return (
    <span style={{
      position: "absolute", width, aspectRatio: "1", borderRadius: "50%",
      filter: "blur(40px)", background: color, opacity, left, right, top, bottom,
    }} />
  );
}

type Variant = "default" | "primary" | "ghost";

/** Opaque buttons: crisp text, not blurred. */
export function GlassButton({
  variant = "default", disabled, onClick, children, fullWidth, type = "button",
}: {
  variant?: Variant; disabled?: boolean; onClick?: () => void; children: React.ReactNode;
  fullWidth?: boolean; type?: "button" | "submit";
}) {
  const b = colors.button[variant];
  return (
    <button
      type={type} onClick={onClick} disabled={disabled}
      style={{
        padding: "10px 16px", borderRadius: tokens.radius.pill,
        border: variant === "ghost" ? "none" : `1px solid ${b.border}`,
        background: b.bg, color: b.fg, fontFamily: sans, fontSize: tokens.type.button.size,
        cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.4 : 1,
        width: fullWidth ? "100%" : undefined, textAlign: "center",
      }}
    >{children}</button>
  );
}

/** A module's own tabs: the floating pill's smaller sibling. */
export function Segment<T extends string>({
  options, value, onChange,
}: { options: { key: T; label: string }[]; value: T; onChange: (v: T) => void }) {
  return (
    <GlassFloat style={{ display: "inline-flex", gap: 2, padding: 4 }}>
      {options.map((o) => {
        const on = o.key === value;
        return (
          <button
            key={o.key} onClick={() => onChange(o.key)}
            style={{
              padding: "6px 12px", borderRadius: 999, border: "none", cursor: "pointer",
              fontFamily: sans, fontSize: tokens.type.segment.size,
              background: on ? colors.accentSoft : "transparent", color: on ? colors.ink : colors.ink2,
            }}
          >{o.label}</button>
        );
      })}
    </GlassFloat>
  );
}

/** A filter chip: a small floating control. On is the soft accent disc, like the nav. */
export function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <GlassFloat style={{ display: "inline-flex", flex: "none", padding: 3 }}>
      <button
        onClick={onClick} aria-pressed={on}
        style={{
          padding: "6px 12px", borderRadius: 999, border: "none", cursor: "pointer", whiteSpace: "nowrap",
          display: "inline-flex", alignItems: "center", gap: 6,
          fontFamily: sans, fontSize: tokens.type.segment.size,
          background: on ? colors.accentSoft : "transparent", color: on ? colors.ink : colors.ink2,
        }}
      >{children}</button>
    </GlassFloat>
  );
}

/** Opaque text input style, shared by the login field, the evening field and the sheets. */
export const inputStyle: React.CSSProperties = {
  width: "100%", boxSizing: "border-box", fontFamily: sans, fontSize: 16, lineHeight: 1.4,
  padding: "10px 12px", borderRadius: 8, border: `1px solid ${colors.hair2}`,
  background: colors.sheet, color: colors.ink,
};
